import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Scaffold' },
  { name: 'R-Groups' },
  { name: 'Enumerate' },
  { name: 'Filter' },
  { name: 'Export' },
];

const PRESETS: Record<string, string[]> = {
  'Common Aryls': ['c1ccccc1', 'c1ccncc1', 'c1ccoc1', 'c1ccsc1', 'c1ccc(F)cc1', 'c1ccc(Cl)cc1'],
  'Heterocycles': ['C1CCNCC1', 'C1CCOC1', 'C1CCNC1', 'c1cn[nH]c1', 'c1ccnc(N)c1'],
  'Bioisosteres': ['C(=O)O', 'C(=O)N', 'S(=O)(=O)N', 'C(F)(F)F', 'C#N'],
};

const WorkbenchLibrary: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [scaffold, setScaffold] = useState('');
  const [rGroupCount, setRGroupCount] = useState(2);
  const [rGroups, setRGroups] = useState<string[]>(['', '']);
  const [maxResults, setMaxResults] = useState(200);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  const [memberSvgs, setMemberSvgs] = useState<Record<string, string>>({});
  const [properties, setProperties] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [scaffoldSvg, setScaffoldSvg] = useState('');
  const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  const { bailOutText, setHasSaved } = useToolStep();

  // Debounced scaffold preview SVG (Steps 0 & 1)
  const [scaffoldPreviewSvg, setScaffoldPreviewSvg] = useState('');
  useEffect(() => {
    if (!scaffold.trim()) { setScaffoldPreviewSvg(''); return; }
    const timer = setTimeout(() => {
      const depictSmi = scaffold.replace(/\[R(\d+)]/g, (_: string, n: string) => `[*:${n}]`);
      apiClient.post('/v2/library/depict', { smiles: [depictSmi], size: 200 })
        .then((resp) => setScaffoldPreviewSvg(resp.data?.svg || ''))
        .catch(() => setScaffoldPreviewSvg(''));
    }, 500);
    return () => clearTimeout(timer);
  }, [scaffold]);

  // Debounced parsed R-group SMILES for thumbnail preview (Step 1)
  const [parsedRGroups, setParsedRGroups] = useState<string[][]>([]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setParsedRGroups(
        rGroups.slice(0, rGroupCount).map((rg) =>
          rg.split(',').map((s) => s.trim()).filter(Boolean)
        )
      );
    }, 500);
    return () => clearTimeout(timer);
  }, [rGroups, rGroupCount]);

  const activeResults = useMemo(
    () => results.map((smi, i) => ({ smi, idx: i })).filter(({ idx }) => !deletedIndices.has(idx)),
    [results, deletedIndices],
  );

  const saveRawData = useMemo(
    () => ({ enumerated_molecules: activeResults.map(({ smi }) => ({ smiles: smi })) }),
    [activeResults],
  );

  const updateRGroup = (idx: number, value: string) => {
    const newGroups = [...rGroups];
    newGroups[idx] = value;
    setRGroups(newGroups);
  };

  const addPreset = (idx: number, smiles: string[]) => {
    const current = rGroups[idx] ? rGroups[idx].split(',').map((s) => s.trim()).filter(Boolean) : [];
    const merged = Array.from(new Set([...current, ...smiles]));
    updateRGroup(idx, merged.join(', '));
  };

  const runEnumeration = async () => {
    setIsRunning(true);
    setError('');
    setResults([]);
    setMemberSvgs({});
    setProperties([]);
    setDeletedIndices(new Set());
    setSelected(new Set());
    setStatusMsg('Enumerating library...');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const parsedGroups = rGroups.slice(0, rGroupCount).map((rg) =>
        rg.split(',').map((s) => s.trim()).filter(Boolean)
      );

      const total = parsedGroups.reduce((acc, g) => acc * Math.max(g.length, 1), 1);
      if (total > 10000) {
        setStatusMsg(`Large library (~${total} combinations). This may take a moment...`);
      }

      // Enumerate
      const enumResp = await apiClient.post('/v2/library/enumerate', {
        scaffold, r_groups: parsedGroups, max_results: maxResults, canonical: true,
      }, { signal: controller.signal });
      const enumData = enumResp.data;
      setResults(enumData.results || []);
      setStatusMsg(`Enumerated ${enumData.returned} / ${enumData.total} compounds. Generating depictions...`);

      // Render individual SVGs (max 50 for grid)
      const depictSmiles: string[] = (enumData.results || []).slice(0, 50);
      if (depictSmiles.length > 0) {
        try {
          const bulkResp = await apiClient.post('/v2/indigo/render_bulk',
            depictSmiles.map((smi: string) => ({ structure: { value: smi, type: 'RAW' }, width: '140px', height: '100px' })),
            { signal: controller.signal },
          );
          if (bulkResp.data && typeof bulkResp.data === 'object') {
            const svgMap: Record<string, string> = {};
            for (const [smi, item] of Object.entries(bulkResp.data as Record<string, { error?: string; result?: string }>)) {
              if (item.result) svgMap[smi] = atob(item.result);
            }
            setMemberSvgs(svgMap);
          }
        } catch { /* depict is optional */ }
      }

      setStatusMsg('Done.');
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError') setError('Enumeration cancelled');
      else setError(err.message || 'Enumeration failed');
      setStatusMsg('');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const downloadResults = () => {
    const smiles = activeResults.map(({ smi }) => smi);
    const blob = new Blob([smiles.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'library_enumeration.smi'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="library" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: RDKit</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Enumeration" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Enter Scaffold</h2>
                <p style={descStyle}>Use [R1], [R2], etc. as attachment point placeholders.</p>
                <div style={{ marginBottom: 8 }}>
                  <button onClick={() => setScaffold('c1ccc([R1])cc1[R2]')} style={btnStyle}>Load Example Scaffold</button>
                </div>
                <textarea value={scaffold} onChange={(e) => setScaffold(e.target.value)}
                  placeholder="Enter scaffold with [R1], [R2] placeholders..." style={textareaStyle} rows={3} />
                {scaffoldPreviewSvg && (
                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 8, marginTop: 8, display: 'inline-block' }}
                    dangerouslySetInnerHTML={{ __html: scaffoldPreviewSvg }} />
                )}
                <button onClick={() => { if (scaffold.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 0]))); setStep(1); } }}
                  disabled={!scaffold.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: scaffold.trim() ? 1 : 0.5 }}>
                  Continue
                </button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Define R-Groups</h2>
                <p style={descStyle}>Enter fragments for each R-group position, comma-separated.</p>
                {scaffoldPreviewSvg && (
                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 8, marginBottom: 12, display: 'inline-block' }}
                    dangerouslySetInnerHTML={{ __html: scaffoldPreviewSvg }} />
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Number of R-groups:{' '}
                    <select value={rGroupCount} onChange={(e) => {
                      const n = parseInt(e.target.value);
                      setRGroupCount(n);
                      setRGroups((prev) => { const next = [...prev]; while (next.length < n) next.push(''); return next.slice(0, n); });
                    }} style={selectStyle}>
                      {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                </div>
                {Array.from({ length: rGroupCount }, (_, i) => (
                  <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ color: '#f59e0b', fontSize: 13, fontWeight: 600, marginBottom: 6, fontFamily: 'monospace' }}>[R{i + 1}]</div>
                    <textarea value={rGroups[i] || ''} onChange={(e) => updateRGroup(i, e.target.value)}
                      placeholder="Enter fragments, comma-separated..." style={{ ...textareaStyle, minHeight: 50 }} rows={2} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      {Object.entries(PRESETS).map(([name, smiles]) => (
                        <button key={name} onClick={() => addPreset(i, smiles)} style={{ ...btnStyle, fontSize: 11, padding: '4px 8px' }}>
                          + {name}
                        </button>
                      ))}
                    </div>
                    {parsedRGroups[i] && parsedRGroups[i].length > 0 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {parsedRGroups[i].map((smi, j) => (
                          <div key={`${smi}-${j}`} style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, padding: 4 }}>
                            <StructureImage smiles={smi} size={60} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Max results:{' '}
                    <input type="number" value={maxResults} onChange={(e) => setMaxResults(parseInt(e.target.value) || 200)}
                      min={1} max={100000} style={{ ...selectStyle, width: 80 }} />
                  </label>
                </div>
                <button onClick={() => {
                  setCompletedSteps((prev) => Array.from(new Set([...prev, 1])));
                  setStep(2);
                  // Fetch scaffold SVG for depiction
                  const depictSmi = scaffold.replace(/\[R(\d+)]/g, (_: string, n: string) => `[*:${n}]`);
                  apiClient.post('/v2/library/depict', { smiles: [depictSmi], size: 240 })
                    .then((resp) => setScaffoldSvg(resp.data?.svg || ''))
                    .catch(() => {});
                }} style={primaryBtnStyle}>
                  Continue to Enumerate
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Enumerate Library</h2>
                <p style={descStyle}>Scaffold: {scaffoldPreviewSvg ? <span style={{ display: 'inline-block', verticalAlign: 'middle', background: 'var(--card-bg)', borderRadius: 8, padding: 4 }} dangerouslySetInnerHTML={{ __html: scaffoldPreviewSvg }} /> : <StructureImage smiles={scaffold} size={48} />}</p>
                {scaffoldSvg && (
                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 8, marginBottom: 12, display: 'inline-block' }}
                    dangerouslySetInnerHTML={{ __html: scaffoldSvg }} />
                )}
                {!isRunning && <button onClick={runEnumeration} style={primaryBtnStyle}>Enumerate</button>}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>{statusMsg}</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 style={h2Style}>Library Results ({activeResults.length} compounds)</h2>

                {/* Toolbar: Select All / Deselect All / Delete Selected */}
                {activeResults.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setSelected(new Set(activeResults.map(({ idx }) => idx)))}
                      style={{ ...btnStyle, fontSize: 12, padding: '5px 10px' }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelected(new Set())}
                      style={{ ...btnStyle, fontSize: 12, padding: '5px 10px' }}
                    >
                      Deselect All
                    </button>
                    {selected.size > 0 && (
                      <button
                        onClick={() => {
                          setDeletedIndices((prev) => {
                            const next = new Set(prev);
                            selected.forEach((i) => next.add(i));
                            return next;
                          });
                          setSelected(new Set());
                        }}
                        style={{ ...btnStyle, fontSize: 12, padding: '5px 10px', color: '#fb7185', borderColor: 'rgba(239,68,68,0.4)' }}
                      >
                        Delete Selected ({selected.size})
                      </button>
                    )}
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12, marginLeft: 4 }}>
                      {selected.size > 0 ? `${selected.size} selected` : ''}
                    </span>
                  </div>
                )}

                {/* Compound card grid */}
                {activeResults.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
                    {activeResults.slice(0, 100).map(({ smi, idx }, displayIdx) => (
                      <div key={idx} style={{
                        position: 'relative',
                        background: selected.has(idx) ? 'rgba(245,158,11,0.08)' : 'var(--card-bg)',
                        border: `1px solid ${selected.has(idx) ? '#f59e0b' : 'var(--card-border)'}`,
                        borderRadius: 10,
                        padding: 8,
                        transition: 'border-color 0.15s, background 0.15s',
                      }}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selected.has(idx)}
                          onChange={() => setSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(idx)) next.delete(idx); else next.add(idx);
                            return next;
                          })}
                          style={{ position: 'absolute', top: 8, left: 8, cursor: 'pointer', accentColor: '#f59e0b' }}
                        />

                        {/* Delete X button */}
                        <button
                          onClick={() => {
                            setDeletedIndices((prev) => new Set(prev).add(idx));
                            setSelected((prev) => { const next = new Set(prev); next.delete(idx); return next; });
                          }}
                          style={{
                            position: 'absolute', top: 4, right: 4,
                            width: 20, height: 20, borderRadius: '50%',
                            border: 'none', background: 'rgba(239,68,68,0.15)', color: '#fb7185',
                            fontSize: 13, lineHeight: '20px', textAlign: 'center',
                            cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          title="Remove compound"
                        >
                          &#x2715;
                        </button>

                        {/* Structure SVG */}
                        <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 4 }}>
                          {memberSvgs[smi] ? (
                            <div dangerouslySetInnerHTML={{ __html: memberSvgs[smi] }} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                          ) : (
                            <StructureImage smiles={smi} size={90} />
                          )}
                        </div>

                        {/* LIB-XXX label */}
                        <div style={{ textAlign: 'center', marginTop: 6, color: '#f59e0b', fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>
                          LIB-{String(displayIdx + 1).padStart(3, '0')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {activeResults.length > 100 && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 12 }}>
                    Showing 100 of {activeResults.length} compounds
                  </div>
                )}

                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 3]))); setStep(4); }} style={primaryBtnStyle}>
                  Continue to Export
                </button>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 style={h2Style}>Export</h2>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => navigator.clipboard.writeText(activeResults.map(({ smi }) => smi).join('\n'))} style={btnStyle}>Copy SMILES</button>
                  <button onClick={downloadResults} style={primaryBtnStyle}>Download .smi</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard
                  isOpen={saveOpen}
                  onClose={() => setSaveOpen(false)}
                  rawData={saveRawData}
                  toolName="Library Enumeration"
                  toolKey="library"
                  smiles={ctxSmiles || scaffold}
                  moleculeId={ctxMoleculeId}
                  fileId={ctxFileId}
                  projectId={ctxProjectId}
                />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard engine="RDKit" title="Combinatorial Enumeration"
              description="Replaces [R1], [R2] placeholders in scaffold SMILES with user-defined R-group fragments. Generates Cartesian product of all combinations."
              equation="Library size = |R1| × |R2| × ... × |Rn|"
              reference="Lipinski et al. (1997) Adv Drug Deliv Rev 23:3-25"
              referenceUrl="https://doi.org/10.1016/S0169-409X(96)00423-1"
              source="RDKit AllChem.ReplaceSidechains" />
            <MethodCard engine="RDKit" title="PAINS Filtering"
              description="Pan Assay Interference Compounds. Substructure filters that flag promiscuous chemical series likely to produce false positives."
              reference="Baell & Holloway (2010) J Med Chem 53:2719-2740"
              referenceUrl="https://doi.org/10.1021/jm901137j"
              source="RDKit FilterCatalog" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box' };
const selectStyle: React.CSSProperties = { padding: '4px 8px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };

export default WorkbenchLibrary;
